import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Clock, Plus, UserPlus, AlertCircle, CheckCircle, MapPin, Home, Calendar, PartyPopper, Briefcase } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { authFetch, API } from '../utils/api';
import { toast } from 'sonner';

const LOCATION_OPTIONS = [
  { value: 'onsite', label: 'On-site', icon: Briefcase, color: 'text-blue-600' },
  { value: 'wfh', label: 'Work From Home', icon: Home, color: 'text-green-600' },
  { value: 'event', label: 'Event', icon: PartyPopper, color: 'text-purple-600' },
  { value: 'field', label: 'Field Work', icon: MapPin, color: 'text-orange-600' }
];

function ManualTimeEntry({ user }) {
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    user_id: '',
    date: new Date().toISOString().split('T')[0],
    clock_in_time: '09:00',
    clock_out_time: '17:00',
    location: 'onsite',
    notes: ''
  });

  useEffect(() => {
    // Redirect if not admin
    if (user.role !== 'admin') {
      toast.error('Access denied. Admin privileges required.');
      navigate('/dashboard');
      return;
    }

    fetchEmployees();
  }, [user, navigate]);

  const fetchEmployees = async () => {
    try {
      const response = await authFetch(`${API}/team/employees`);
      if (response.ok) {
        const data = await response.json();
        // Sort employees by name
        const sorted = data.sort((a, b) => a.name.localeCompare(b.name));
        setEmployees(sorted);
      } else {
        toast.error('Failed to load employees');
      }
    } catch (error) {
      console.error('Error fetching employees:', error);
      toast.error('Error loading employees');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.user_id) {
      toast.error('Please select an employee');
      return;
    }

    if (!formData.date || !formData.clock_in_time || !formData.clock_out_time) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Combine date and time into ISO datetime strings
    const clockIn = `${formData.date}T${formData.clock_in_time}:00`;
    const clockOut = `${formData.date}T${formData.clock_out_time}:00`;

    // Validate time order
    if (new Date(clockOut) <= new Date(clockIn)) {
      toast.error('Clock out time must be after clock in time');
      return;
    }

    setSubmitting(true);

    try {
      const response = await authFetch(`${API}/time/admin/create-entry`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: formData.user_id,
          clock_in: clockIn,
          clock_out: clockOut,
          location: formData.location,
          notes: formData.notes || undefined
        }),
      });

      if (response.ok) {
        const data = await response.json();
        const employee = employees.find(e => e.user_id === formData.user_id);
        toast.success(`Time entry created successfully for ${employee?.name}`);

        // Reset form but keep the selected employee
        setFormData({
          ...formData,
          date: new Date().toISOString().split('T')[0],
          clock_in_time: '09:00',
          clock_out_time: '17:00',
          location: 'onsite',
          notes: ''
        });
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create time entry');
      }
    } catch (error) {
      console.error('Error creating time entry:', error);
      toast.error('Error creating time entry');
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const calculateDuration = () => {
    if (!formData.date || !formData.clock_in_time || !formData.clock_out_time) {
      return '';
    }

    const clockIn = new Date(`${formData.date}T${formData.clock_in_time}:00`);
    const clockOut = new Date(`${formData.date}T${formData.clock_out_time}:00`);

    if (clockOut <= clockIn) {
      return 'Invalid time range';
    }

    const diffMs = clockOut - clockIn;
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

    return `${hours}h ${minutes}m`;
  };

  if (loading) {
    return (
      <Layout user={user}>
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Loading...</div>
        </div>
      </Layout>
    );
  }

  const selectedEmployee = employees.find(e => e.user_id === formData.user_id);
  const duration = calculateDuration();

  return (
    <Layout user={user}>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <Clock className="w-8 h-8 text-indigo-600" />
                Manual Time Entry
              </h1>
              <p className="text-gray-600 mt-1">
                Add time in/out records retroactively for employees
              </p>
            </div>
            <div className="bg-indigo-50 px-4 py-2 rounded-lg">
              <p className="text-xs text-indigo-600 font-medium">ADMIN ONLY</p>
            </div>
          </div>
        </div>

        {/* Form */}
        <div className="bg-white rounded-lg shadow p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Employee Selection */}
            <div>
              <Label htmlFor="employee" className="text-base font-medium">
                Employee *
              </Label>
              <Select
                value={formData.user_id}
                onValueChange={(value) => handleChange('user_id', value)}
              >
                <SelectTrigger className="mt-2">
                  <SelectValue placeholder="Select an employee" />
                </SelectTrigger>
                <SelectContent>
                  {employees.map((employee) => (
                    <SelectItem key={employee.user_id} value={employee.user_id}>
                      <div className="flex items-center gap-2">
                        <span>{employee.name}</span>
                        <span className="text-xs text-gray-500">({employee.email})</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedEmployee && (
                <p className="mt-2 text-sm text-gray-600">
                  Role: <span className="font-medium">{selectedEmployee.role}</span>
                </p>
              )}
            </div>

            {/* Date */}
            <div>
              <Label htmlFor="date" className="text-base font-medium">
                Date *
              </Label>
              <input
                type="date"
                id="date"
                value={formData.date}
                onChange={(e) => handleChange('date', e.target.value)}
                max={new Date().toISOString().split('T')[0]}
                className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                required
              />
            </div>

            {/* Time In and Out */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="clock_in_time" className="text-base font-medium">
                  Clock In Time *
                </Label>
                <input
                  type="time"
                  id="clock_in_time"
                  value={formData.clock_in_time}
                  onChange={(e) => handleChange('clock_in_time', e.target.value)}
                  className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  required
                />
              </div>

              <div>
                <Label htmlFor="clock_out_time" className="text-base font-medium">
                  Clock Out Time *
                </Label>
                <input
                  type="time"
                  id="clock_out_time"
                  value={formData.clock_out_time}
                  onChange={(e) => handleChange('clock_out_time', e.target.value)}
                  className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  required
                />
              </div>
            </div>

            {/* Duration Display */}
            {duration && (
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">Total Duration:</span>
                  <span className={`text-lg font-bold ${duration === 'Invalid time range' ? 'text-red-600' : 'text-indigo-600'}`}>
                    {duration}
                  </span>
                </div>
              </div>
            )}

            {/* Location */}
            <div>
              <Label htmlFor="location" className="text-base font-medium">
                Location *
              </Label>
              <Select
                value={formData.location}
                onValueChange={(value) => handleChange('location', value)}
              >
                <SelectTrigger className="mt-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LOCATION_OPTIONS.map((option) => {
                    const Icon = option.icon;
                    return (
                      <SelectItem key={option.value} value={option.value}>
                        <div className="flex items-center gap-2">
                          <Icon className={`w-4 h-4 ${option.color}`} />
                          <span>{option.label}</span>
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            {/* Notes */}
            <div>
              <Label htmlFor="notes" className="text-base font-medium">
                Notes (Optional)
              </Label>
              <textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => handleChange('notes', e.target.value)}
                rows={3}
                placeholder="Add any relevant notes about this time entry..."
                className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              <p className="mt-1 text-xs text-gray-500">
                If no note is provided, the system will automatically add "Added by admin: [Your Name]"
              </p>
            </div>

            {/* Info Box */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex gap-3">
              <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-1">Important Notes:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>This will create a completed time entry for the selected employee</li>
                  <li>The system will check for overlapping entries and prevent conflicts</li>
                  <li>All entries created through this form will be marked as admin-created</li>
                  <li>You can only create entries for past or current dates</li>
                </ul>
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex gap-3 pt-4">
              <Button
                type="submit"
                disabled={submitting || !formData.user_id || duration === 'Invalid time range'}
                className="flex-1"
              >
                {submitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    Creating Entry...
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4 mr-2" />
                    Create Time Entry
                  </>
                )}
              </Button>

              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/reports')}
              >
                Cancel
              </Button>
            </div>
          </form>
        </div>
      </div>
    </Layout>
  );
}

export default ManualTimeEntry;
